/**
 * Ce que les tests de bout en bout ont le droit de demander au jeu.
 *
 * POURQUOI CE MODULE EXISTE. `client/jeu.ts` finissait par un demi-millier de
 * lignes qui ne servent jamais a jouer: des passerelles pour les tests, avec
 * leurs longues explications. Elles brouillaient la lecture de la boucle sans
 * rien lui apporter, et c'etait pourtant le fichier que presque chaque
 * modification devait ouvrir.
 *
 * Ne sont descendues ici que les passerelles qui CALCULENT quelque chose a
 * partir d'arguments explicites: elles deviennent verifiables sans navigateur.
 * Celles qui ne font que lire un champ prive, ou qui allument un mode du jeu,
 * restent dans la classe — les sortir aurait demande de rendre publique la
 * moitie de son etat, ce qui aurait coute plus que ca ne rapporte.
 *
 * Les methodes de `Jeu` restent en place et se contentent d'appeler ce qui suit:
 * les tests continuent d'ecrire `__jeu.pointMarchableTest(...)`.
 */
import type { Animal, Batiment, BatimentKind, Ressource } from '../shared/types/monde.ts'
import type { Inventaire } from '../shared/types/items.ts'
import type { ITerrain, BiomeId, Vec2 } from '../shared/types/world.ts'
import type { EtatPresence } from '../shared/types/reseau.ts'
import { WORLD_SIZE } from '../shared/constants.ts'

import type { GrilleNav } from '../shared/sim/navigation.ts'
import type { SuiviFaune } from '../shared/sim/faune-reseau.ts'
import { enclosAuPoint } from '../shared/sim/zoo.ts'
import {
  cheminGlissade,
  glissadesDe,
  porteExterieur,
  porteInterieur,
  portePosition,
  positionAnimal,
  positionCuisine,
  pisteDe,
  positionEscalier,
  positionFilon,
  positionLit,
  positionPortable,
  positionRestaurant,
  positionSalon,
  positionSpa,
  positionTable,
  validerPlacement,
} from '../shared/sim/batiments.ts'

/** Ce que le jeu reconnait comme enclos a un point donne. */
export interface EnclosVu {
  /** Vrai s'il y a bien un enclos de zoo ici. */
  trouve: boolean
  /** Nom de l'enclos, ou la chaine vide. */
  nom: string
  rayon: number
  capacite: number
}

/**
 * Sans cette passerelle, un test de bout en bout ne peut pas savoir s'il se
 * tient dans un enclos: il installe a l'aveugle et echoue plus loin, sur une
 * consequence. Elle interroge exactement ce que le jeu interroge.
 */
export function enclosAutour(batiments: readonly Batiment[], x: number, z: number): EnclosVu {
  const e = enclosAuPoint(batiments, x, z)
  if (!e) return { trouve: false, nom: '', rayon: 0, capacite: 0 }
  return { trouve: true, nom: e.nom, rayon: e.rayon, capacite: e.capacite }
}

/**
 * Un endroit ou ce modele, dans CETTE rotation, peut reellement etre pose.
 *
 * POURQUOI CETTE PASSERELLE EXISTE. Un test qui ecrit une coordonnee en dur
 * mesure la geographie avant de mesurer ce qu'il annonce. Celui des rotations
 * de villa partait d'un secteur cotier fixe: mesure faite, il y existe UN seul
 * emplacement valable en rotation 0 et 2, et AUCUN en 1 et 3 — une villa fait
 * 12 x 9, tournee d'un quart de tour elle fait 9 x 12, et cette orientation ne
 * rentre nulle part par la. Deux tests echouaient donc en permanence sur la
 * forme du terrain, pas sur les portes qu'ils pretendaient verifier.
 *
 * Chercher plus large ne suffisait pas: sur douze points de foret, un balayage
 * de 70 m ne trouve les quatre rotations que deux fois, et 120 m dix fois. La
 * bonne question n'est pas « ou marcher ? » mais « ou BATIR ? », et seul le jeu
 * peut y repondre — c'est sa propre validation qui tranche ici, pas une
 * approximation ecrite dans le test.
 */
export function pointConstructible(
  kind: BatimentKind,
  rot: number,
  terrain: ITerrain,
  batiments: Batiment[],
  inventaire: Inventaire,
  ressources: Ressource[],
): Vec2 | null {
  for (let x = 24; x < WORLD_SIZE - 24; x += 6) {
    for (let z = 24; z < WORLD_SIZE - 24; z += 6) {
      const v = validerPlacement(kind, { x, z }, rot, terrain, batiments, inventaire, ressources, undefined)
      if (v.ok) return { x, z }
    }
  }
  return null
}

/**
 * Un point marchable et plat dans le biome demande.
 *
 * Les tests visaient des coordonnees ecrites en dur, si bien qu'un changement
 * de relief les envoyait sur une falaise — et le filet anti-blocage les
 * deplacait sans qu'ils s'en apercoivent (47 sauvetages releves a un seul
 * point). On demande donc au terrain.
 */
export function pointMarchable(terrain: ITerrain, grille: GrilleNav, biome: BiomeId): Vec2 | null {
  let meilleur: Vec2 | null = null
  let meilleurePente = Infinity
  for (let x = 12; x < WORLD_SIZE - 12; x += 4) {
    for (let z = 12; z < WORLD_SIZE - 12; z += 4) {
      if (terrain.biome(x, z) !== biome) continue
      if (!grille.estLibreMonde(x, z)) continue
      const pente = terrain.pente(x, z)
      if (pente < meilleurePente) {
        meilleurePente = pente
        meilleur = { x, z }
        if (pente < 0.05) return meilleur
      }
    }
  }
  return meilleur
}

export interface JoueurVu {
  id: string
  nom: string
  couleur: number
  x: number
  z: number
  etat: string
}

/** Une etiquette de joueur distant, telle que le rendu la publie. */
export interface EtiquetteVue {
  id: string
  nom: string
  couleur: number
  x: number
  z: number
}

/**
 * Les autres joueurs tels qu'on les voit en ce moment. Passerelle de test:
 * un test de bout en bout a deux navigateurs a besoin de verifier que l'un
 * voit l'autre BOUGER, ce qu'une capture d'ecran ne dit pas.
 */
export function autresJoueurs(
  etiquettes: readonly EtiquetteVue[],
  etats: ReadonlyMap<string, EtatPresence>,
): JoueurVu[] {
  const sortie: JoueurVu[] = []
  for (const e of etiquettes) {
    const etat = etats.get(e.id)
    sortie.push({
      id: e.id,
      nom: e.nom,
      couleur: e.couleur,
      x: e.x,
      z: e.z,
      etat: etat?.etat ?? 'immobile',
    })
  }
  return sortie
}

export interface AnimalVu {
  id: number
  kind: string
  x: number
  z: number
  /** Ce que le SERVEUR a dit en dernier, avant lissage. Null en solo. */
  dit: { x: number; z: number } | null
  etat: string
  source: string
}

/**
 * Un animal tel que CE client le voit, avec la source de l'information.
 *
 * C'est la passerelle qui rend l'autorite serveur verifiable: trois
 * navigateurs qui interrogent le meme identifiant doivent trouver la meme
 * bete au meme endroit. Sans cette vue, la difference entre « chacun simule
 * dans son coin » et « le serveur simule pour tout le monde » ne se voit sur
 * aucune capture d'ecran.
 */
export function animalVu(
  animaux: readonly Animal[],
  id: number,
  suiviFaune: SuiviFaune,
  fauneRecue: boolean,
): AnimalVu | null {
  const a = animaux[id]
  if (!a) return null
  return {
    id: a.id,
    kind: a.kind,
    x: a.pos.x,
    z: a.pos.z,
    // `x/z` est la position DESSINEE, donc lissee, donc en retard de ce que le
    // rendu local met a suivre; `dit` est la parole du serveur. Comparer deux
    // clients sur `x/z` mesure surtout leurs cadences d'images respectives —
    // en rendu logiciel a une image par seconde, cela fait des metres.
    dit: suiviFaune.positionDe(a.id),
    etat: a.etat,
    source: fauneRecue ? 'serveur' : 'local',
  }
}

export interface PointsBatiment {
  centre: Vec2
  porte: Vec2
  porteExterieur: Vec2
  porteInterieur: Vec2
  cuisine: Vec2
  lit: Vec2
  salon: Vec2
  table: Vec2
  animal: Vec2
  /**
   * Les trois points de la terrasse. Publies ICI plutot que recopies dans les
   * tests: un test qui redit les fractions du plan continue de taper au meme
   * endroit le jour ou le plan bouge, et passe en visant le vide.
   *
   * `pointEtage` repond pour n'importe quel modele, donc ces champs existent
   * meme sans etage — ils ne designent simplement rien d'interessant.
   */
  escalier: Vec2
  portable: Vec2
  spa: Vec2
  /**
   * Le front de taille de la mine, meme raison que les trois precedents: un
   * test qui recopierait la fraction de `ANCRAGE_MEUBLE` continuerait de viser
   * l'ancien endroit le jour ou le plan bouge.
   */
  filon: Vec2
  /**
   * LA PISTE d'un aeroport: son milieu, son point de stationnement, et le
   * comptoir du restaurant a l'etage. `null` pour tout ce qui n'est pas un
   * aeroport — la question n'a alors pas de sens, et un test qui recevrait un
   * point quand meme viserait le vide.
   */
  piste: Vec2 | null
  parking: Vec2 | null
  restaurant: Vec2 | null
  /**
   * LES GLISSADES du parc aquatique: leur nom, leur seuil sur la passerelle et
   * leur arrivee dans le bassin. Vide pour tout autre modele.
   *
   * Publiees ici et non recopiees dans les tests, pour la meme raison que les
   * trois points de la terrasse: un test qui redirait les fractions du plan
   * continuerait de taper au meme endroit le jour ou le plan bouge, et passerait
   * en visant le vide. C'est `cheminGlissade` — celle que la descente emploie —
   * qui repond.
   */
  glissades: { nom: string; depart: Vec2; arrivee: Vec2 }[]
}

/** Points remarquables d'un batiment, pour les tests. */
export function pointsBatiment(b: Batiment): PointsBatiment {
  return {
    centre: b.pos,
    porte: portePosition(b),
    porteExterieur: porteExterieur(b),
    porteInterieur: porteInterieur(b),
    cuisine: positionCuisine(b),
    lit: positionLit(b),
    salon: positionSalon(b),
    table: positionTable(b),
    animal: positionAnimal(b),
    escalier: positionEscalier(b),
    portable: positionPortable(b),
    spa: positionSpa(b),
    filon: positionFilon(b),
    glissades: glissadesDeBatiment(b),
    ...pointsAeroport(b),
  }
}

/** Les glissades d'un batiment, en coordonnees du monde. Vide s'il n'en a pas. */
function glissadesDeBatiment(b: Batiment): { nom: string; depart: Vec2; arrivee: Vec2 }[] {
  const sortie: { nom: string; depart: Vec2; arrivee: Vec2 }[] = []
  for (const [i, g] of glissadesDe(b.kind).entries()) {
    const chemin = cheminGlissade(b, i)
    if (chemin) sortie.push({ nom: g.nom, depart: chemin.depart, arrivee: chemin.arrivee })
  }
  return sortie
}

/** Les points d'un aeroport, ou trois `null` pour tout autre modele. */
function pointsAeroport(b: Batiment): { piste: Vec2 | null; parking: Vec2 | null; restaurant: Vec2 | null } {
  const piste = pisteDe(b)
  if (!piste) return { piste: null, parking: null, restaurant: null }
  return {
    piste: piste.centre,
    parking: piste.parking,
    restaurant: positionRestaurant(b),
  }
}
